import { Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { Chart } from 'chart.js/auto'

const INFRA_DATA: Record<string, { title: string; hardware: string; specs: string; desc: string }> = {
  test: {
    title: '测试与功能验证环境',
    hardware: '入门级企业 NAS / 标准云端实例',
    specs: '2核 CPU | 4G 内存 | 本地 RAID 1 阵列',
    desc: '适合早期功能验证，跑通单个 Agent 在无害沙箱中的本地单一自动化流程。',
  },
  dept: {
    title: '部门级应用支撑',
    hardware: '中端企业存储 / 云端计算优化实例',
    specs: '4核 CPU | 8G 内存 | 本地 NVMe 高速缓存',
    desc: '支撑多 Agent 协作流，应对中等并发请求，如 HR 自动化报表抓取与审批流分发。',
  },
  enterprise: {
    title: '企业级高并发中枢',
    hardware: '高性能分布式 NAS / 云端异构计算节点',
    specs: '4核 16G+ 控制面 | 独立企业级 GPU 推理加速',
    desc: '承载全公司跨部门任务调度，处理海量数据汇聚分析，具备完整高可用与灾备能力。',
  },
}

const SECURITY_STEPS = [
  { title: '1. 权限最小化运行', desc: '严格以非 root 账号身份运行核心引擎进程与所有生成的沙箱 Docker 容器。' },
  { title: '2. 密钥层层硬化', desc: '严禁在任何脚本或源码中硬编码 API Key，必须通过环境变量文件或对接企业级 KMS 凭证管理系统注入。' },
  { title: '3. 物理网段隔离', desc: '生产环境中，Agent 所有出入站流量必须强制经过内部透明代理服务器，用于拦截和清洗敏感信息。' },
  { title: '4. 第三方技能审计', desc: '任何外部提供的第三方 Skill 代码，在合并进主运行分支前，必须进行强制的 SAST（静态代码扫描）。' },
  { title: '5. 运行时目录锁定', desc: '引擎一旦启动，即刻将核心配置文件与依赖树设置为只读模式，防止被恶意动态篡改。' },
  { title: '6. 本地每日快照', desc: '依托本地企业级存储设备的特性，建立每日静默自动数据卷回滚点，确保数据安全底线。' },
  { title: '7. 监管日志脱敏', desc: '对于汇聚到审计员面板的所有系统日志，必须针对手机号、账号等敏感 PII 数据执行自动掩码替换。' },
  { title: '8. 容器逃逸防御', desc: '制定定期基线检查策略，自动扫描并应用沙箱底层内核补丁，彻底封堵微型 VM 被恶意指令穿透的风险。' },
]

export function Enterprise() {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const [infraTab, setInfraTab] = useState<'test' | 'dept' | 'enterprise'>('test')
  const [rbacOpen, setRbacOpen] = useState<string | null>(null)
  const [accordionOpen, setAccordionOpen] = useState<number | null>(null)

  useEffect(() => {
    if (!chartRef.current) return
    const ctx = chartRef.current.getContext('2d')
    if (!ctx) return
    const chart = new Chart(ctx, {
      type: 'radar',
      data: {
        labels: ['架构安全性', '业务敏捷度', '部署运维效率', '定制灵活性', '代码透明度'],
        datasets: [
          {
            label: 'NanoClaw (容器隔离架构)',
            data: [95, 90, 85, 90, 95],
            fill: true,
            backgroundColor: 'rgba(230, 48, 34, 0.2)',
            borderColor: 'rgb(230, 48, 34)',
            pointBackgroundColor: 'rgb(230, 48, 34)',
            pointBorderColor: '#fff',
            borderWidth: 2,
          },
          {
            label: '传统 Open 架构',
            data: [45, 60, 40, 65, 30],
            fill: true,
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            borderColor: 'rgb(120, 120, 120)',
            pointBackgroundColor: 'rgb(120, 120, 120)',
            pointBorderColor: '#fff',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          r: {
            min: 0,
            max: 100,
            ticks: { display: false },
            pointLabels: { font: { size: 11 } },
          },
        },
        plugins: {
          legend: { position: 'bottom' },
        },
      },
    })
    return () => chart.destroy()
  }, [])

  return (
    <div className="page enterprise-page">
      <h1 className="section-title">企业部署服务解决方案</h1>
      <div className="enterprise-intro">
        <p className="lead" style={{ marginBottom: '0.75rem' }}>
          企业场景建议采用 <strong>NanoClaw</strong>：更轻量、更安全，专为隔离与合规设计，比通用 OpenClaw 更贴合企业需求。
        </p>
        <p className="roadmap-lead" style={{ marginBottom: '2rem' }}>
          本页介绍 NanoClaw 私有化部署、协同办公与通道对接，一站式方案与支持；部署与通道实操可沿用本站 OpenClaw 生态文档。
        </p>
      </div>

      {/* 战略愿景：数字员工中枢 */}
      <section className="enterprise-roadmap-section" id="vision">
        <h2>构建企业的数字员工中枢</h2>
        <p className="roadmap-lead">
          在当前的 AI 浪潮中，核心价值已从单一的“对话工具”进化为“执行系统”。传统的聊天机器人仅能检索信息，而以 <strong>NanoClaw</strong> 为底座的 AI Agent 具备了任务规划与自主执行的闭环能力。
        </p>
        <div className="enterprise-vision-grid">
          <div className="enterprise-vision-card">
            <h3>技能胜于功能 (Skills over Features)</h3>
            <p>Agent 能够理解如“完成季度招聘数据审计并输出内部报表”等复杂目标，并自主拆解为抓取、比对、排版与分发的执行流，取代步进式的指令输入。</p>
          </div>
          <div className="enterprise-vision-card">
            <h3>组织架构进化</h3>
            <p>引入“零薪水数字员工”接管高重复、标准化的业务链，实现 24/7 不间断运作，推动企业向智能驱动的敏捷组织迈进，真正将 AI 嵌入私有业务流程。</p>
          </div>
        </div>
      </section>

      {/* 架构比对 */}
      <section className="enterprise-roadmap-section" id="architecture">
        <h2>为什么企业更适合 NanoClaw？</h2>
        <p className="roadmap-lead">
          企业级应用必须在功能与攻击面之间做权衡。NanoClaw 采用“通过隔离实现安全 (Safe by Isolation)”的哲学，极简代码与沙箱隔离，比通用 OpenClaw 更贴合企业安全与审计需求。
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' }}>
          <div className="enterprise-chart-wrap">
            <canvas ref={chartRef} />
          </div>
          <ul className="enterprise-compare-list">
            <li className="accent">极简代码 vs. 配置膨胀：一个进程、少量核心文件，“代码即配置”，极易全量安全审计。</li>
            <li className="accent">Hypervisor 级沙箱隔离：Agent 仅能访问显式挂载的目录，杜绝提示词注入导致的主机失控。</li>
            <li className="accent">派生即定制 (Forks as Channels)：通过代码分支注入技能，业务逻辑透明且防篡改。</li>
          </ul>
        </div>
      </section>

      {/* 基础设施：存算分离 */}
      <section className="enterprise-roadmap-section" id="infrastructure">
        <h2>基础设施：存算分离方案</h2>
        <p className="roadmap-lead">
          企业级部署必须遵循计算与存储分离。本地企业级 NAS 作为数据持久化与隐私防护底座，云端提供弹性调度的推理算力。
        </p>
        <div className="enterprise-tabs">
          {(['test', 'dept', 'enterprise'] as const).map((id) => (
            <button
              key={id}
              type="button"
              className={infraTab === id ? 'active' : ''}
              onClick={() => setInfraTab(id)}
            >
              {id === 'test' && '1. 测试与功能验证期'}
              {id === 'dept' && '2. 部门级多体协同'}
              {id === 'enterprise' && '3. 企业级计算中枢'}
            </button>
          ))}
        </div>
        <div className="enterprise-tab-panel">
          {(() => {
            const d = INFRA_DATA[infraTab]
            return (
              <>
                <h3>{d.title}</h3>
                <p className="roadmap-lead" style={{ marginBottom: '0.5rem' }}>配置基准：{d.hardware}</p>
                <div className="specs">{d.specs}</div>
                <p className="roadmap-lead" style={{ marginBottom: 0 }}>{d.desc}</p>
              </>
            )
          })()}
        </div>
        <div className="enterprise-note">
          <strong>本地 NAS 安全底座逻辑</strong>
          通过 VLAN 网络隔离，存储层置于独立网段。利用硬件级 RAID 与数据快照，为自主执行的 Agent 提供秒级回滚能力，防止 Agent 幻觉或误操作删除核心业务数据。
        </div>
      </section>

      {/* 多租户与权限 */}
      <section className="enterprise-roadmap-section" id="rbac">
        <h2>多租户架构与权限隔离</h2>
        <p className="roadmap-lead">
          在多部门环境下，利用隔离组上下文与容器挂载技术，实现严格边界隔离。即使 HR 部门的 Agent 失控，也无法跨越文件系统边界读取财务数据。
        </p>
        <div className="enterprise-rbac-grid">
          <div className="enterprise-rbac-card" onClick={() => setRbacOpen(rbacOpen === 'admin' ? null : 'admin')}>
            <h3>管理员 (Admin)</h3>
            <ul>
              <li>全局 Skill 审计与下发</li>
              <li>API 密钥池与额度管理</li>
              <li>容器集群热迁移</li>
            </ul>
            <div className={`role-detail ${rbacOpen === 'admin' ? 'open' : ''}`}>
              <strong>核心能力：</strong>完全修改 CLAUDE.md（定义 Agent 的灵魂指令与核心身份，防范员工私自更改 Agent 设定）。
            </div>
          </div>
          <div className="enterprise-rbac-card" onClick={() => setRbacOpen(rbacOpen === 'emp' ? null : 'emp')}>
            <h3>普通员工</h3>
            <ul>
              <li>调用所属部门专属 Skill</li>
              <li>发起日常自动化任务流</li>
              <li>查看自有任务输出结果</li>
            </ul>
            <div className={`role-detail ${rbacOpen === 'emp' ? 'open' : ''}`}>
              <strong>记忆限制：</strong>对系统记忆文件仅拥有只读/追加权限，允许 Agent 记录工作摘要，但禁止重写系统级指令。
            </div>
          </div>
          <div className="enterprise-rbac-card" onClick={() => setRbacOpen(rbacOpen === 'auditor' ? null : 'auditor')}>
            <h3>审计员</h3>
            <ul>
              <li>查看跨部门全链路日志</li>
              <li>监控 Token 消耗与成本</li>
              <li>敏感信息泄露拦截检查</li>
            </ul>
            <div className={`role-detail ${rbacOpen === 'auditor' ? 'open' : ''}`}>
              <strong>隔离逻辑：</strong>对 Agent 运行内存禁止访问。审计日志进行 PII 脱敏掩码。
            </div>
          </div>
        </div>
      </section>

      {/* 实施路线图：三步走 */}
      <section className="enterprise-roadmap-section" id="deployment">
        <h2>企业级实施路线图：三步走</h2>
        <p className="roadmap-lead">
          依托 DevOps 自动化思维，实现 15 分钟极速标准化交付。摒弃传统图形面板，拥抱 AI-Native 运维理念。
        </p>
        <div className="enterprise-timeline">
          <div className="enterprise-timeline-step">
            <h3>准备阶段：环境硬化与凭证获取</h3>
            <div className="step-grid">
              <div className="step-item">
                <strong>环境预检</strong>
                <p>部署 Node.js 22+ 运行时，启用 Native Performance 底层优化。</p>
              </div>
              <div className="step-item">
                <strong>凭证路由</strong>
                <p>订阅企业级大模型服务商的高可用推理服务，获取并安全存储 API Key。</p>
              </div>
              <div className="step-item" style={{ gridColumn: '1 / -1' }}>
                <strong>本地安全策略</strong>
                <p>为核心目录配置防病毒软件的实时扫描排除项，防止高频 IPC 被误判拦截导致服务卡顿。</p>
              </div>
            </div>
          </div>
          <div className="enterprise-timeline-step">
            <h3>部署阶段：镜像化与自动化交付</h3>
            <div className="step-grid">
              <div className="step-item">
                <strong>自动化编排</strong>
                <p>编写统一的容器编排脚本 (Compose)，一键镜像拉取、隔离网络创建与环境变量动态注入。</p>
              </div>
              <div className="step-item">
                <strong>安全网关限制</strong>
                <p>仅开放指定通信端口，防火墙层配置基于内部 CIDR 的 IP 白名单访问策略。</p>
              </div>
              <div className="step-item" style={{ gridColumn: '1 / -1' }}>
                <strong>AI-Native 调试范式</strong>
                <p>摒弃复杂运维监控面板。系统异常时，直接运行原生调试指令让 AI 诊断其自身的运行环境配置冲突。</p>
              </div>
            </div>
          </div>
          <div className="enterprise-timeline-step">
            <h3>扩展阶段：技能注入与可观测性</h3>
            <div className="step-grid">
              <div className="step-item">
                <strong>动态扩展 (Skill 注入)</strong>
                <p>通过标准化指令，按需向各部门分支中动态注入企业即时通讯、企业邮箱等外部 API 集成技能。</p>
              </div>
              <div className="step-item">
                <strong>深度可观测性</strong>
                <p>系统全量接入企业级云原生标准监控体系，大屏实时监控推理延迟、Token 消耗及容器负载。</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 安全加固与成本治理 */}
      <section className="enterprise-roadmap-section" id="governance">
        <h2>安全加固与成本治理</h2>
        <p className="roadmap-lead">
          将 AI 从“玩具”转变为可控的“数字资产”，需要在系统级边界和 Token 经济学两方面进行严苛治理。
        </p>
        <div className="enterprise-governance-grid">
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem' }}>八大系统级安全加固规范</h3>
            <div>
              {SECURITY_STEPS.map((step, i) => (
                <div
                  key={i}
                  className="enterprise-accordion-item"
                  onClick={() => setAccordionOpen(accordionOpen === i ? null : i)}
                >
                  {step.title}
                  <p className={`accordion-desc ${accordionOpen === i ? 'open' : ''}`}>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="enterprise-token-card">
            <h3>Token 成本与上下文治理</h3>
            <div className="token-item">
              <h4>1. QMD 记忆压缩系统</h4>
              <p>这是 NanoClaw 的核心治理工具。长时间运行的 Agent 会导致上下文窗口膨胀。通过语义压缩旧对话日志，可节省 60% 以上的 API 推理成本，且不丢失关键逻辑。</p>
            </div>
            <div className="token-item">
              <h4>2. Heartbeat 智能休眠</h4>
              <p>配置最大空闲时间阈值（如 30 分钟）。无任务时容器进入挂起状态，自动释放云端昂贵的算力长连接资源。</p>
            </div>
            <div className="token-item">
              <h4>3. 硬性算力约束</h4>
              <p>为非核心后台流程设置严格的单次 maxTokens 输出限制，防止 Agent 因陷入逻辑死循环而导致的巨额账单爆炸。</p>
            </div>
          </div>
        </div>
      </section>

      {/* 原有卡片：私有化部署、协同、安全、技能 */}
      <div className="card">
        <h2>私有化部署</h2>
        <p>企业推荐 NanoClaw 内网/专有云部署，数据不出域。Docker 或 K8s 部署，配置 <code>shm_size: '2gb'</code> 与 <code>18789</code> 端口，详见部署专区。</p>
        <p><Link to="/deploy">→ 部署专区</Link></p>
      </div>

      <div className="card">
        <h2>国内企业协同办公</h2>
        <p>飞书、钉钉、企业微信等通道对接，将 NanoClaw / OpenClaw 作为内部协同办公助理。配置 OAuth、机器人与权限策略；通道与插件可参考本站 OpenClaw 文档。</p>
        <ul className="guide-links">
          <li><Link to="/channels">通道对接（飞书、Telegram、Discord 等）</Link></li>
          <li><Link to="/guides/enterprise">国内企业协同办公助理 (飞书等) 场景指南</Link></li>
        </ul>
      </div>

      <div className="card">
        <h2>安全与合规</h2>
        <p>沙盒配置、密钥管理、<code>openclaw doctor</code> 排障与安全红线说明。</p>
        <p><Link to="/sec-fix">→ 安全与排障</Link></p>
      </div>

      <div className="card">
        <h2>技能与定制</h2>
        <p>企业常用 Skills 选型、ClawHub 安全使用与自定义技能开发指引。</p>
        <p><Link to="/skills">→ 技能市场选品</Link></p>
      </div>

      <section className="enterprise-roadmap-section" aria-label="服务方案与报价">
        <h2>服务方案与报价</h2>
        <div className="enterprise-plans">
          <div className="enterprise-plan-card">
            <h3 className="enterprise-plan-title">标准方案</h3>
            <p className="enterprise-plan-price">20,000 起</p>
            <p className="enterprise-plan-desc">内网/专有云部署、基础配置与文档支持，适合中小团队快速落地。</p>
          </div>
          <div className="enterprise-plan-card">
            <h3 className="enterprise-plan-title">专业方案</h3>
            <p className="enterprise-plan-price">50,000 起</p>
            <p className="enterprise-plan-desc">含飞书/钉钉/企微对接、协同办公配置与一定周期内的专属支持。</p>
          </div>
          <div className="enterprise-plan-card enterprise-plan-card-custom">
            <h3 className="enterprise-plan-title">定制方案</h3>
            <p className="enterprise-plan-price">定制</p>
            <p className="enterprise-plan-desc">按需定制部署规模、通道数量、技能开发与 SLA，联系洽谈。</p>
          </div>
        </div>
      </section>

      <section className="enterprise-contact" aria-label="联系方式">
        <h2>联系方式</h2>
        <p className="enterprise-contact-lab">Limen AI Lab</p>
        <p className="enterprise-contact-name">里门AI老杨</p>
        <p className="enterprise-contact-wechat">微信：yubin69</p>
      </section>
    </div>
  )
}
